import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import i18n from '../i18n/config';
import facebookAuth from '../services/facebookAuth';

export const AuthContext = createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      // Check for Facebook OAuth callback first
      let facebookCallbackHandled = false;
      try {
        const fb = await facebookAuth.handleFacebookCallback();
        if (fb) {
          // Facebook OAuth callback detected - stored token in localStorage
          facebookCallbackHandled = true;
          try {
            navigate('/', { replace: true });
          } catch (e) {}
        }
      } catch (e) {
        console.error('Facebook OAuth error:', e);
      }

      // Only process Supabase OAuth if Facebook callback wasn't detected
      if (!facebookCallbackHandled) {
        const oauthSession = await supabase.handleOAuthCallback();
        if (oauthSession) {
          setUser(supabase.getUser());
          try {
            navigate('/', { replace: true });
          } catch (e) {}
        } else {
          const session = supabase.getSession();
          if (session) {
            setUser(supabase.getUser());
          }
        }
      }

      setLoading(false);
    };
    checkAuth();
  }, [navigate]);

  const signIn = async (email, password) => {
    const result = await supabase.signIn(email, password);
    if (result.user) {
      setUser(result.user);
    }
    return result;
  };

  const signUp = async (email, password) => {
    const result = await supabase.signUp(email, password);
    return result;
  };

  const signInWithProvider = async (provider) => {
    await supabase.signInWithOAuth(provider);
  };

  const signOut = async () => {
    // Clear all platform tokens from localStorage only (keep in Supabase for next login)
    localStorage.removeItem('facebook_token');
    localStorage.removeItem('youtube_token');
    localStorage.removeItem('tiktok_auth');
    
    // Sign out from Supabase (this clears the auth session)
    await supabase.signOut();
    setUser(null);
  };

  const updatePassword = async (newPassword) => {
    return await supabase.updatePassword(newPassword);
  };

  const sendPasswordResetEmail = async (email) => {
    return await supabase.sendPasswordResetEmail(email);
  };

  const deleteAccount = async () => {
    try {
      // Delete the user account (this will cascade delete all related data)
      await supabase.deleteAccount();
      
      // Clear state and redirect to login
      setUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(i18n.t('app.profile.deleteAccountError', { defaultValue: 'Hiba történt a fiók törlése során: {{message}}', message: error.message }));
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signInWithProvider, signOut, updatePassword, sendPasswordResetEmail, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export { AuthProvider };
